import { Link } from "react-router-dom";
import sheepMascot from "@/assets/landing/sheep-mascot-teal.png";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO, PRODUCTS, ProductKey } from "./tokens";

export interface SiteFooterProps {
  /** Current surface — its product link is marked, not linked away. */
  product?: ProductKey;
  /** "rich" keeps the homepage wordmark + mascot ornament. */
  variant?: "rich" | "plain";
  /** Lead source for the footer call button. */
  callSource?: string;
}

const colHead: React.CSSProperties = {
  fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.16em",
  textTransform: "uppercase", color: PALETTE.muted, marginBottom: 14, display: "block",
};

const colLink: React.CSSProperties = {
  color: PALETTE.ink2, textDecoration: "none", fontSize: 13.5, display: "block", padding: "5px 0",
};

export function SiteFooter({ product, variant = "plain", callSource = "footer" }: SiteFooterProps) {
  const rich = variant === "rich";
  return (
    <footer
      data-footer
      data-site-footer
      style={{
        padding: rich ? "40px 24px 60px" : "48px 24px 60px",
        background: PALETTE.bgAlt,
        borderTop: `1px solid ${PALETTE.line}`,
        position: "relative",
        overflow: "hidden",
        fontFamily: FONT_BODY,
      }}
    >
      {rich && (
        <>
          <div data-eva-wordmark aria-hidden>Eva.</div>
          <img
            src={sheepMascot} alt="" aria-hidden width={90} height={90} loading="lazy" decoding="async"
            style={{
              position: "absolute", left: -16, bottom: -16, width: 90, height: "auto",
              opacity: 0.05, pointerEvents: "none", userSelect: "none", zIndex: 0,
            }}
          />
        </>
      )}

      {/* Zone 1+2 — product / company columns */}
      <div
        className="site-footer-cols"
        style={{
          maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1,
          display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 28,
          paddingBottom: 26, borderBottom: `1px solid ${PALETTE.line}`,
        }}
      >
        <div>
          <span style={colHead}>Products</span>
          {PRODUCTS.map((p) => {
            const isCurrent = p.key === product;
            return (
              <Link
                key={p.key}
                to={p.to}
                aria-current={isCurrent ? "page" : undefined}
                style={{ ...colLink, color: isCurrent ? PALETTE.ink : PALETTE.ink2, fontWeight: isCurrent ? 600 : 400 }}
              >
                {p.label}
              </Link>
            );
          })}
        </div>
        <div>
          <span style={colHead}>Company</span>
          <button
            type="button"
            onClick={() => openCallMeNow(callSource)}
            style={{ ...colLink, background: "transparent", border: 0, cursor: "pointer", font: "inherit", fontSize: 13.5, textAlign: "left", padding: "5px 0" }}
          >
            Talk to Us Now
          </button>
          <Link to="/ambassadors" style={colLink}>Ambassadors</Link>
          <Link to="/status" style={colLink}>Status</Link>
        </div>
      </div>

      {/* Zone 3 — human trust line */}
      <div
        data-footer-human
        style={{
          maxWidth: 1100, margin: "22px auto 0", position: "relative", zIndex: 1,
          textAlign: "center", fontSize: 12.5, color: PALETTE.muted, lineHeight: 1.5,
        }}
      >
        <span style={{ display: "inline-block", maxWidth: 520 }}>
          Built by a small team across SF, New York, and Tokyo. We read every reply.
          <br />
          <a href="mailto:support@electricsheephq.com" style={{ color: PALETTE.ink2, textDecoration: "underline", textUnderlineOffset: 3 }}>
            support@electricsheephq.com
          </a>
        </span>
        <div style={{ marginTop: 10, fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 17, color: PALETTE.ink2 }}>
          — the ElectricSheep team
        </div>
      </div>

      {/* Zone 4 — legal floor */}
      <div
        data-footer-bottom
        style={{
          maxWidth: 1100, margin: "24px auto 0", position: "relative", zIndex: 1,
          display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 20,
        }}
      >
        <span data-footer-brand style={{ fontSize: 12, color: PALETTE.muted }}>
          evaOS · © {new Date().getFullYear()} ElectricSheep, Inc.
        </span>
        <div data-footer-legal style={{ display: "flex", flexWrap: "wrap", gap: 22, fontSize: 13, color: PALETTE.muted }}>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>Privacy</Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>Terms</Link>
          <Link to="/engineering-terms" style={{ color: "inherit", textDecoration: "none" }}>Engineering Terms</Link>
          <Link to="/velocity-plans" style={{ color: "inherit", textDecoration: "none" }}>Velocity Plans</Link>
          <Link to="/cookie-policy" style={{ color: "inherit", textDecoration: "none" }}>Cookies</Link>
        </div>
      </div>

      <style>{`
        [data-site-footer] a:hover { color: ${PALETTE.ink}; }
        @media (max-width: 560px) {
          .site-footer-cols { grid-template-columns: 1fr !important; gap: 20px !important; text-align: center !important; }
          [data-site-footer] [data-footer-bottom] { flex-direction: column !important; justify-content: center !important; text-align: center !important; }
          [data-site-footer] [data-footer-legal] { justify-content: center !important; width: 100% !important; }
          .site-footer-cols button { text-align: center !important; width: 100% !important; }
        }
      `}</style>
    </footer>
  );
}

export default SiteFooter;